import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import logo from './wysupp-icon-green-transparent.svg';
import AccountInfo from './AccountInfo';
import axiosInstance from './api/axios';
import { FaArrowLeft } from 'react-icons/fa';

function UserEmailPreferences() {
    const navigate = useNavigate();
    const [projectPreferences, setProjectPreferences] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [successMessage, setSuccessMessage] = useState('');

    const fetchPreferences = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const response = await axiosInstance.get('/api/v1/email-preferences/projects');
            setProjectPreferences(response.data);
            setIsLoading(false); // Stop loading on success
        } catch (err) {
            // --- TIMEOUT FIX: Play dead on 401 ---
            if (err.response?.status === 401) return;
            
            console.error("Error fetching preferences:", err);
            setError("Could not load your preferences. Please try again later.");
            setIsLoading(false); // Stop loading on actual errors
        }
    }, []);

    useEffect(() => {
        fetchPreferences();
    }, [fetchPreferences]);

    const handlePreferenceChange = async (projectId, newValue) => {
        const originalPreferences = [...projectPreferences];
        setProjectPreferences(prev => 
            prev.map(p => 
                p.project_id === projectId 
                ? { ...p, receber_notificacoes_alteracoes_interface: newValue } 
                : p
            )
        );
        setSuccessMessage('');

        try {
            await axiosInstance.put(`/api/v1/email-preferences/projects/${projectId}`, { 
                receber_notificacoes_alteracoes_interface: newValue 
            });
            
            setSuccessMessage('Preference saved successfully!');
            setTimeout(() => setSuccessMessage(''), 3000);

        } catch (err) {
            // --- TIMEOUT FIX: Prevent toggle reverting & error flash on timeout ---
            if (err.response?.status === 401) return;
            
            console.error("Error saving preference:", err);
            setError('Could not save your change. Please try again.');
            setProjectPreferences(originalPreferences);
        }
    };

    return (
        <div className='admin-page-container'>
            <header className="dashboard-header">
                <div className="logo-section">
                    <img src={logo} className="logo-icon" alt="Wysupp Logo" />
                    <span className="logo-text">Wysupp Validate</span>
                    <span className="logo-subtext">Data Quality Management</span>
                </div>
                <AccountInfo />
            </header>
            <main className="admin-main-content">
                <div className="page-header">
                    <button onClick={() => navigate('/home')} className="back-button">
                        <FaArrowLeft /> Back
                    </button>
                    <h1 className="page-title">Email Preferences</h1>
                </div>
                
                {isLoading ? (
                    <div className="loading-state">
                        <div className="loading-spinner"></div>
                        <p>Loading preferences...</p>
                    </div>
                ) : (
                    <div className="preferences-wrapper">
                        <p className="main-subtitle">Manage notifications for interface changes for each of your projects.</p>
                        
                        {error && <div className="form-feedback error">{error}</div>}
                        {successMessage && <div className="form-feedback success">{successMessage}</div>}
                        
                        <div className="preferences-list">
                            {projectPreferences.length > 0 ? (
                                projectPreferences.map(pref => (
                                    <div key={pref.project_id} className="preference-item">
                                        <span className="project-name">{pref.project_name}</span>
                                        <label className="switch">
                                            <input
                                                type="checkbox"
                                                checked={pref.receber_notificacoes_alteracoes_interface}
                                                onChange={(e) => handlePreferenceChange(pref.project_id, e.target.checked)}
                                            />
                                            <span className="slider round"></span>
                                        </label>
                                    </div>
                                ))
                            ) : (
                                <p className="no-projects-message">You are not assigned to any projects with configurable notifications.</p>
                            )}
                        </div>
                    </div>
                )}
            </main>
            <style>{`
                :root {
                    --dark-bg: #1a202c; --card-bg: #2d3748; --text-color: #e2e8f0; --light-text: #a0aec0;
                    --green-accent: #48bb78; --border-color: #4a5568; --input-bg: #1a202c;
                    --error-color: #fca5a5; --error-bg: rgba(239, 68, 68, 0.1);
                    --success-color: #a7f3d0; --success-bg: rgba(52, 211, 153, 0.1);
                }

                .admin-page-container {
                    background-color: var(--dark-bg);
                    min-height: 100vh;
                    color: var(--text-color);
                    font-family: 'Inter', sans-serif;
                }

                .dashboard-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    padding: 1rem 3rem;
                    background-color: var(--card-bg);
                    border-bottom: 1px solid var(--border-color);
                }
                .logo-section { display: flex; align-items: center; gap: 0.5rem; }
                .logo-icon { width: 30px; height: 30px; }
                .logo-text { font-size: 1.25rem; font-weight: 700; color: var(--green-accent); }
                .logo-subtext { font-size: 0.75rem; color: var(--light-text); margin-left: 0.5rem; padding-left: 0.5rem; border-left: 1px solid var(--light-text); }
                
                .admin-main-content {
                    max-width: 800px;
                    margin: 2rem auto;
                    padding: 0 2rem;
                }

                .page-header {
                    display: flex;
                    align-items: center;
                    gap: 1rem;
                    margin-bottom: 1.5rem;
                }
                .back-button {
                    background: var(--card-bg); color: var(--text-color); border: 1px solid var(--border-color);
                    padding: 0.5rem 1rem; border-radius: 6px; font-weight: 600; cursor: pointer;
                    transition: background-color 0.2s; display: flex; align-items: center; gap: 0.5rem;
                }
                .back-button:hover { background: #4a5568; }
                .page-title {
                    font-size: 2rem;
                    font-weight: 700;
                    color: var(--text-color);
                }

                .preferences-wrapper {
                    background-color: var(--card-bg);
                    border: 1px solid var(--border-color);
                    border-radius: 12px;
                    padding: 2.5rem;
                }
                
                .main-subtitle {
                    font-size: 1rem;
                    color: var(--light-text);
                    margin-top: 0;
                    margin-bottom: 2rem;
                    text-align: center;
                }

                .form-feedback {
                    width: 100%; padding: 0.75rem; border-radius: 6px;
                    font-size: 0.9rem; font-weight: 500;
                    margin-bottom: 2rem; text-align: center;
                }
                .form-feedback.success { background-color: var(--success-bg); color: var(--success-color); }
                .form-feedback.error { background-color: var(--error-bg); color: var(--error-color); }

                .preferences-list {
                    display: flex;
                    flex-direction: column;
                    gap: 0.5rem;
                }
                .preference-item {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    padding: 1rem;
                    border-radius: 8px;
                    border: 1px solid var(--border-color);
                    background-color: var(--input-bg);
                }
                .project-name {
                    font-weight: 500;
                    font-size: 1.1rem;
                }
                .no-projects-message {
                    text-align: center;
                    color: var(--light-text);
                    font-style: italic;
                    padding: 2rem;
                }
                
                /* The switch - the box around the slider */
                .switch {
                    position: relative;
                    display: inline-block;
                    width: 50px; /* smaller width */
                    height: 28px; /* smaller height */
                }

                /* Hide default HTML checkbox */
                .switch input {
                    opacity: 0;
                    width: 0;
                    height: 0;
                }

                /* The slider */
                .slider {
                    position: absolute;
                    cursor: pointer;
                    top: 0;
                    left: 0;
                    right: 0;
                    bottom: 0;
                    background-color: var(--border-color);
                    -webkit-transition: .4s;
                    transition: .4s;
                }

                .slider:before {
                    position: absolute;
                    content: "";
                    height: 20px; /* smaller circle */
                    width: 20px; /* smaller circle */
                    left: 4px;
                    bottom: 4px;
                    background-color: white;
                    -webkit-transition: .4s;
                    transition: .4s;
                }

                input:checked + .slider {
                    background-color: var(--green-accent);
                }

                input:focus + .slider {
                    box-shadow: 0 0 1px var(--green-accent);
                }

                input:checked + .slider:before {
                    -webkit-transform: translateX(22px); /* adjusted translation */
                    -ms-transform: translateX(22px);
                    transform: translateX(22px);
                }

                /* Rounded sliders */
                .slider.round {
                    border-radius: 34px;
                }

                .slider.round:before {
                    border-radius: 50%;
                }

                .loading-state { text-align: center; color: var(--text-color); padding-top: 5rem; }
                .loading-spinner {
                    width: 40px; height: 40px; margin: 0 auto 1rem;
                    border: 4px solid var(--border-color); border-top-color: var(--green-accent);
                    border-radius: 50%; animation: spin 1s linear infinite;
                }
                @keyframes spin { to { transform: rotate(360deg); } }
            `}</style>
        </div>
    );
}

export default UserEmailPreferences;